/**
 * Scan du plan pour l'analyse — collecte les entités candidates au
 * regroupement local (cf. `utils/plan-analysis/grouping.ts`).
 *
 * Une entité est candidate si elle porte un DR_META, n'est pas du fond de plan
 * (`background`), n'est pas déjà rattachée à une observation (`observationId`)
 * et n'est pas masquée (calque UNDO). Un stamp ORPHELIN — observation
 * supprimée depuis le bureau ou un autre appareil, le plan n'en sait rien —
 * est ignoré quand l'appelant fournit la liste des observations vivantes :
 * l'entité redevient candidate (le stamp sera écrasé à la prochaine
 * validation, rien n'est écrit ici). La MÊME traversée calcule l'emprise du
 * plan entier (fond de plan compris) : c'est la référence d'échelle des seuils
 * relatifs du regroupement.
 *
 * Best-effort, comme `listObservationEntities` : une entité illisible
 * n'interrompt jamais le scan.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type {
  AnalysisEntity,
  BackgroundItem,
  WorldBbox,
} from "@/utils/plan-analysis/types";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { openAs, getEntityHandle } from "../core/oda";
import { getModelSpaceId } from "../parsing/viewports";
import { UNDO_LAYER_NAME } from "../database/layers";
import { readEntityMeta } from "./xdata";
import { getPhotoMarkerAttributeValue } from "../photomarker/transform";
import { getPhotoMarkerShape } from "../photomarker/detection";
import { readKindAndText } from "../parsing/activity";
import { measureEntity } from "./measure";
import { readEntityBbox } from "./extents";
import { sampleEntityGeometry } from "./geometry-sample";

export interface AnalysisScanOptions {
  /**
   * `localId` des observations existantes de l'ouvrage. Absent → toute
   * entité estampée est exclue (comportement prudent).
   */
  liveObservationIds?: ReadonlySet<string>;
}

/** Résultat du scan : candidates + fond de plan + emprise du plan entier. */
export interface AnalysisScanResult {
  entities: AnalysisEntity[];
  /** Entités estampées : exclues (observation vivante) / réintégrées (stamp orphelin). */
  excluded: { stamped: number; stale: number };
  /**
   * Emprises du FOND DE PLAN (entités sans DR_META, ou stampées background),
   * avec le texte pour les entités texte — matière première de la détection
   * des vignettes (`utils/plan-analysis/vignettes.ts`).
   */
  backgroundItems: BackgroundItem[];
  /** Emprise monde de TOUTES les entités du plan, null si plan vide. */
  planBbox: WorldBbox | null;
}

/** Numéro du marqueur : seulement si le libellé EST un nombre (cf. observation.ts). */
function extractNumber(text: string | null): number | null {
  if (!text) return null;
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? parseInt(trimmed, 10) : null;
}

/**
 * Pointes des leaders d'un MLeader (premier sommet de chaque ligne) — c'est LÀ
 * que le texte pointe, bien plus fiable que sa bbox pour le rattachement.
 * Les index de lignes peuvent être troués : on sonde au-delà de numLeaderLines
 * (même workaround éprouvé que LeaderLineCommand.findLeaderLineByArrowTip).
 */
function readLeaderTips(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
): Array<{ x: number; y: number }> | null {
  let mleader: DrawingModule.OdDbMLeader | null = null;
  try {
    mleader = module.OdDbMLeader.cast(entity);
    if (!mleader) return null;
    const numLines = mleader.numLeaderLines();
    if (numLines <= 0) return null;
    const tips: Array<{ x: number; y: number }> = [];
    const maxIndexToTry = numLines + 10;
    for (
      let lineIndex = 0;
      lineIndex < maxIndexToTry && tips.length < numLines;
      lineIndex++
    ) {
      const point = new module.OdGePoint3d(0, 0, 0);
      const result = mleader.getFirstVertex(lineIndex, point);
      if (result === 0) tips.push({ x: point.x, y: point.y });
      deleteObj(point);
    }
    return tips.length > 0 ? tips : null;
  } catch {
    return null;
  } finally {
    if (mleader) deleteObj(mleader);
  }
}

/**
 * Longueur d'une polyligne SIMPLE (≤ 6 sommets) : candidate « cadre de
 * vignette » (un rectangle a longueur ≈ périmètre de sa bbox). Les
 * polylignes complexes du fond sont écartées dès `numVerts` — le scan reste
 * linéaire même sur un gros fond de plan.
 */
function readFrameLength(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
): number | null {
  let poly: DrawingModule.OdDbPolyline | null = null;
  try {
    poly = module.OdDbPolyline.cast(entity);
    if (!poly) return null;
    const numVerts = poly.numVerts();
    if (numVerts < 4 || numVerts > 6) return null;
    const pt = new module.OdGePoint2d(0, 0);
    const verts: Array<{ x: number; y: number }> = [];
    for (let i = 0; i < numVerts; i++) {
      poly.getPointAt(i, pt); // rempli par référence
      verts.push({ x: pt.x, y: pt.y });
    }
    deleteObj(pt);
    let length = 0;
    for (let i = 1; i < verts.length; i++) {
      length += Math.hypot(verts[i].x - verts[i - 1].x, verts[i].y - verts[i - 1].y);
    }
    if (poly.isClosed()) {
      const last = verts[verts.length - 1];
      length += Math.hypot(verts[0].x - last.x, verts[0].y - last.y);
    }
    return length;
  } catch {
    return null;
  } finally {
    if (poly) deleteObj(poly);
  }
}

/**
 * Pointe de la flèche dans le repère LOCAL des blocs marqueurs
 * PHOTO_LARGE_ARROW / PHOTO_THIN_ARROW : (0, 2) — cf. blocks/photomarker.ts.
 * À faire évoluer si la géométrie des blocs change.
 */
const ARROW_TIP_LOCAL_Y = 2;

/**
 * Visée d'un marqueur photo flèche en mode bloc : base = point d'insertion,
 * pointe = insertion + rotation + échelle appliquées au point local (0, 2).
 * La pointe est l'ancre de rattachement, la base l'origine du cône de visée.
 */
function readMarkerAim(
  module: DrawingModuleType,
  entity: DrawingModule.OdDbEntity,
): { base: { x: number; y: number }; tip: { x: number; y: number } } | null {
  let blockRef: DrawingModule.OdDbBlockReference | null = null;
  try {
    blockRef = module.OdDbBlockReference.cast(entity);
    if (!blockRef) return null;
    const pos = blockRef.position();
    const rotation = blockRef.rotation();
    const scale = blockRef.scaleFactors();
    const reach = ARROW_TIP_LOCAL_Y * Math.abs(scale.sy || 1);
    const aim = {
      base: { x: pos.x, y: pos.y },
      tip: {
        x: pos.x - Math.sin(rotation) * reach,
        y: pos.y + Math.cos(rotation) * reach,
      },
    };
    deleteObj(pos);
    deleteObj(scale);
    return aim;
  } catch {
    return null;
  } finally {
    if (blockRef) deleteObj(blockRef);
  }
}

/**
 * Scanne l'espace objet : entités candidates à l'analyse + emprise du plan.
 * UNE seule traversée, aucune écriture.
 */
export function scanAnalysisCandidates(
  viewer: DRDrawingViewer,
  options: AnalysisScanOptions = {},
): AnalysisScanResult {
  const module = viewer.module;
  const entities: AnalysisEntity[] = [];
  const backgroundItems: BackgroundItem[] = [];
  const excluded = { stamped: 0, stale: 0 };
  let planBbox: WorldBbox | null = null;

  const modelSpaceId = getModelSpaceId(viewer);
  if (!modelSpaceId) return { entities, backgroundItems, planBbox, excluded };

  const rec = openAs(
    modelSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  // Traitement d'UNE entité, sorti de la boucle : un `return` ici ne peut pas
  // sauter le `iter.step()` — un `continue` dans le while le ferait, et
  // l'itérateur figé bouclerait à l'infini (gel de l'app, vécu).
  const visitEntity = (
    id: DrawingModule.OdDbObjectId,
    ent: DrawingModule.OdDbEntity,
  ): void => {
    const layerStr = ent.layer();
    const layer = layerStr.toString();
    deleteObj(layerStr);
    if (layer === UNDO_LAYER_NAME) return;

    const bbox = readEntityBbox(module, ent);
    // L'emprise du plan intègre TOUT (fond de plan compris) — c'est la
    // taille réelle du dessin qui calibre les seuils relatifs.
    if (bbox) {
      planBbox = planBbox
        ? {
            minX: Math.min(planBbox.minX, bbox.minX),
            minY: Math.min(planBbox.minY, bbox.minY),
            maxX: Math.max(planBbox.maxX, bbox.maxX),
            maxY: Math.max(planBbox.maxY, bbox.maxY),
          }
        : bbox;
    }

    const meta = readEntityMeta(module, id);
    // Fond de plan (pas de DR_META, ou stampé background à la volée) : on ne
    // garde que l'emprise — et le texte, candidat titre de vignette.
    if (!meta || meta.background) {
      if (bbox) {
        const text = readKindAndText(module, id, ent).text ?? null;
        const length = text ? null : readFrameLength(module, ent);
        backgroundItems.push({
          bbox,
          ...(text ? { text } : {}),
          ...(length !== null ? { length } : {}),
        });
      }
      return;
    }
    if (meta.observationId) {
      const live = options.liveObservationIds;
      if (!live || live.has(meta.observationId)) {
        excluded.stamped++;
        return;
      }
      excluded.stale++;
    }

    const measure = measureEntity(module, ent);
    const markerLabel = getPhotoMarkerAttributeValue(module, id);
    // Forme du marqueur (sémantique flèche/rond) — seulement si c'en est un.
    const markerShape =
      markerLabel !== null ? getPhotoMarkerShape(module, id) : null;
    const aim = markerShape?.includes("arrow")
      ? readMarkerAim(module, ent)
      : null;
    // Géométrie réelle des tracés seulement : textes et marqueurs se mesurent
    // par leur bbox (et leur visée pour les flèches).
    const sampled =
      markerLabel === null ? sampleEntityGeometry(module, ent) : null;
    entities.push({
      handle: getEntityHandle(id),
      entityUid: meta.entityUid,
      kind: meta.kind,
      defectName: meta.defectName,
      createdAt: meta.createdAt,
      layer,
      bbox,
      length: measure.length,
      area: measure.area,
      orientation: measure.orientation,
      text: readKindAndText(module, id, ent).text ?? null,
      markerNumber: extractNumber(markerLabel),
      markerLabel,
      markerShape,
      markerTip: aim?.tip ?? null,
      markerBase: aim?.base ?? null,
      leaderTips: readLeaderTips(module, ent),
      ...(sampled ? { points: sampled.points, closed: sampled.closed } : {}),
    });
  };

  const iter = rec.newIterator(true, true, false);
  iter.start(true, true);
  while (!iter.done()) {
    const id = iter.objectId();
    try {
      const ent = iter.entity(OpenMode.kForRead, false);
      try {
        visitEntity(id, ent);
      } finally {
        deleteObj(ent);
      }
    } catch {
      // entité illisible — on ignore
    }
    iter.step(true, true);
  }
  deleteObj(iter);
  deleteObj(rec);

  return { entities, backgroundItems, planBbox, excluded };
}

/**
 * Index `entityUid` → handle courant de TOUTES les entités DR_META (y compris
 * rattachées — il sert aussi aux groupes validés). Les handles ne survivent
 * pas aux round-trips de sauvegarde, l'uid si : c'est la clé de réhydratation
 * d'une analyse persistée.
 */
export function mapAnalysisEntityHandles(
  viewer: DRDrawingViewer,
): Map<string, string> {
  const module = viewer.module;
  const map = new Map<string, string>();
  const modelSpaceId = getModelSpaceId(viewer);
  if (!modelSpaceId) return map;

  const rec = openAs(
    modelSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  const iter = rec.newIterator(true, true, false);
  iter.start(true, true);
  while (!iter.done()) {
    const id = iter.objectId();
    try {
      const meta = readEntityMeta(module, id);
      if (meta) map.set(meta.entityUid, getEntityHandle(id));
    } catch {
      // entité illisible — on ignore
    }
    iter.step(true, true);
  }
  deleteObj(iter);
  deleteObj(rec);

  return map;
}
